import { useI18n } from '@masar/i18n/react';
import { formatAmount, formatAmount0 } from '@masar/i18n';
import {
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type TableHTMLAttributes,
} from 'react';
import { cx } from './cx';
import { Icon, type IconName } from './icons/Icon';
import s from './display.module.css';

export const displayStyles = s;

/* ---------------- Chips (toggle buttons) ---------------- */

export function Chips({
  children,
  className,
  label,
}: {
  children: ReactNode;
  className?: string;
  label?: string;
}) {
  return (
    <div className={cx(s.chips, className)} role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function Chip(
  props: ButtonHTMLAttributes<HTMLButtonElement> & { pressed?: boolean; icon?: IconName },
) {
  const { pressed, icon, className, children, type = 'button', ...rest } = props;
  return (
    <button type={type} className={cx(s.chip, className)} aria-pressed={pressed} {...rest}>
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
    </button>
  );
}

/* ---------------- Segmented control ---------------- */

export function Segmented<T extends string>(props: {
  value: T;
  options: readonly { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div className={cx(s.seg, props.className)} role="group" aria-label={props.label}>
      {props.options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={props.value === o.value}
          onClick={() => props.onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Status pill ---------------- */

export type Tone = 'ok' | 'warn' | 'crit' | 'info' | 'mute' | 'brand';

export function Pill({
  tone = 'mute',
  children,
  className,
  icon,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  icon?: IconName;
}) {
  return (
    <span className={cx(s.pill, s[tone], className)}>
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
    </span>
  );
}

/* ---------------- Cards ---------------- */

export function Card({
  className,
  children,
  as: As = 'section',
  ...rest
}: HTMLAttributes<HTMLElement> & { as?: 'section' | 'div' | 'article' | 'aside' }) {
  return (
    <As className={cx(s.card, className)} {...rest}>
      {children}
    </As>
  );
}

export function CardHeader({
  title,
  icon,
  aside,
  className,
}: {
  title: ReactNode;
  icon?: IconName;
  aside?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(s.cardH, className)}>
      <h3>
        {icon ? <Icon name={icon} size="sm" /> : null}
        {title}
      </h3>
      {typeof aside === 'string' ? <span>{aside}</span> : aside}
    </div>
  );
}

/* ---------------- KPI ---------------- */

export function Kpis({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={cx(s.kpis, className)} style={style}>
      {children}
    </div>
  );
}

/**
 * Plain numbers read left to right in an isolated run; anything else (money
 * with its currency, Arabic dates and words) keeps the page direction so
 * "117.000 ر.ع" and "7 أكتوبر" read like the prototype.
 */
export function kpiValueIsPlain(value: ReactNode): boolean {
  return typeof value === 'number' || (typeof value === 'string' && !/[\u0600-\u06FF]/.test(value));
}

export function Kpi(props: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  icon?: IconName;
  highlight?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(s.kpi, props.highlight && s.hl, props.className)}>
      {props.icon ? (
        <i className={s.kpiIc}>
          <Icon name={props.icon} />
        </i>
      ) : null}
      <span>{props.label}</span>
      <b className={kpiValueIsPlain(props.value) ? 'num' : undefined}>{props.value}</b>
      {props.sub !== undefined ? <small>{props.sub}</small> : null}
    </div>
  );
}

/* ---------------- Page header ---------------- */

export function PageHeader({
  title,
  eyebrow,
  actions,
  className,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(s.pageH, className)}>
      <div>
        {eyebrow ? <p>{eyebrow}</p> : null}
        <h1>{title}</h1>
      </div>
      {actions}
    </div>
  );
}

/* ---------------- Empty state & skeleton ---------------- */

export function EmptyState({
  children,
  action,
  className,
}: {
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(s.empty, className)}>
      <div>{children}</div>
      {action}
    </div>
  );
}

export function Skeleton({
  width = '100%',
  height = 16,
  className,
  style,
}: {
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
}) {
  return <span className={cx(s.skel, className)} style={{ width, height, ...style }} aria-hidden="true" />;
}

/** A few skeleton lines while a list loads; announced once for screen readers. */
export function SkeletonList({ rows = 3, height = 54 }: { rows?: number; height?: number }) {
  const { t } = useI18n();
  return (
    <div style={{ display: 'grid', gap: 10 }} role="status" aria-label={t('جارٍ التحميل…')}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={height} />
      ))}
    </div>
  );
}

/* ---------------- Money ---------------- */

/**
 * An amount in baisa as the prototype prints it: <span class="num">142.000</span> <span class="cur">ر.ع</span>.
 * null/undefined (hidden by the API for staff without f.price.view) shows "•••".
 */
export function Money({
  baisa,
  whole,
  className,
}: {
  baisa: number | null | undefined;
  whole?: boolean;
  className?: string;
}) {
  const { cur } = useI18n();
  if (baisa === null || baisa === undefined) return <span className={cx('muted', className)}>•••</span>;
  return (
    <span className={className}>
      <span className="num">{whole ? formatAmount0(baisa) : formatAmount(baisa)}</span>{' '}
      <span className="cur">{cur}</span>
    </span>
  );
}

/* ---------------- Tabs ---------------- */

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
  badge?: number;
}

/**
 * Underlined tabs with arrow-key navigation (WAI-ARIA tabs pattern).
 * Render the panel yourself with id `${idBase}-panel-${value}` if needed.
 */
export function Tabs<T extends string>(props: {
  value: T;
  items: readonly TabItem<T>[];
  onChange: (v: T) => void;
  label: string;
  className?: string;
  idBase?: string;
}) {
  const { dir } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = props.items.findIndex((x) => x.value === props.value);
    const n = props.items.length;
    let next = i;
    const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    else if (e.key === forward) next = (i + 1) % n;
    else next = (i - 1 + n) % n;
    const item = props.items[next];
    if (!item) return;
    props.onChange(item.value);
    ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div ref={ref} className={cx(s.tabs, props.className)} role="tablist" aria-label={props.label}>
      {props.items.map((it) => {
        const sel = it.value === props.value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            id={props.idBase ? `${props.idBase}-tab-${it.value}` : undefined}
            aria-controls={props.idBase ? `${props.idBase}-panel-${it.value}` : undefined}
            aria-selected={sel}
            tabIndex={sel ? 0 : -1}
            onClick={() => props.onChange(it.value)}
            onKeyDown={onKey}
          >
            {it.icon ? <Icon name={it.icon} size="sm" /> : null}
            {it.label}
            {it.badge ? <span className={cx(s.tabBadge, 'num')}>{it.badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Table ---------------- */

/** Plain data table; `cardify` turns rows into cards on phones (cells need data-label). */
export function Table({
  cardify,
  className,
  children,
  ...rest
}: TableHTMLAttributes<HTMLTableElement> & { cardify?: boolean }) {
  return (
    <div className={s.tableWrap}>
      <table className={cx(s.table, cardify && s.cardify, className)} {...rest}>
        {children}
      </table>
    </div>
  );
}
